import {
  FinancialFormDocsEnum,
  FinancialFormStatusEnum,
  FinancialFormTypeEnum,
  VehiclePurposeEnum,
} from "models/financialForm/enums/financialForm.enums";
import {
  TFinancialInformation,
  TFullAddress,
  TPersonalInformation,
  TPersonalReferences,
} from "models/shared/interfaces/shared.interface";
import { IncodeSessionStatus } from "models/incode/interfaces/incode.interface";
import { ITVehicle } from "../../inventory/interfaces/vehicle.interface";
import { IPreOffer } from "../../loanOffer/interfaces/preOffer.interface";
import { Ozoner } from "../../ozoner/interfaces/ozoner.interface";

export interface FormAddDocumentRequestDelete {
  formId: string;
  documentId: string;
}

export interface FormAddDocumentRequestPut {
  formId: string;
  formData: FormData;
  documentName: string;
}

export interface StatusHistoric {
  status: FinancialFormStatusEnum;
  updatedAt: string;
  by: string;
}

export interface ITaxData {
  rfc?: string;
  zipCode?: string;
  regimeDescription?: string;
  regimeCode?: number;
  rfcName?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface IEvidenceDocument {
  _id: string;
  name: string;
  url: string;
  size: number;
  path: string;
  hash: string;
  ext: string;
  mimeType: string;
  typeEvidence: FinancialFormDocsEnum;
}

export interface IIncodeSession {
  status: IncodeSessionStatus;
  completedAt?: string;
}

export interface IFinancialFormOffer {
  profileTier?: string;
  duration: number;
  downPaymentPct: number;
  downPaymentAmount: number;
  ticketPrice: number;
  financingAmount: number;
  periodicPayment: number;
  expirationDate: string;
  currency: string;
  createdAt: string;
  whitelist?: string;
}

export interface ILoanApplication {
  loanDuration: number;
  downPayment?: number;
  rejectedReason?: string;
  devolutionReason?: string;
  partnership?: string;
  loanSegmentation: string;
  sellResponsible: string;
  discountId: string;
  salesRank: string;
  appointment: {
    scheduledAt: string;
    deliverOzonio: string;
  };
  contactMethod: string;
  isResponseSuccess: boolean;
  preOffers?: IPreOffer[];
}

interface IBaseFinancialForm {
  _id: string;
  status: FinancialFormStatusEnum;
  ozoner: Ozoner;
  vehicle?: ITVehicle;
  sku?: string;
  skuVariant?: string;
  documents: IEvidenceDocument[];
  statusHistoric: StatusHistoric[];
  analyticsIds?: string[];
  city: string;
  createdAt: string;
  updatedAt: string;
  vehiclePurpose?: VehiclePurposeEnum;
  offer?: IFinancialFormOffer;
  taxData?: ITaxData;
}

export interface ILoanFinancialForm extends IBaseFinancialForm {
  type: FinancialFormTypeEnum.LOAN;
  personalInformation: TPersonalInformation;
  financialInformation: TFinancialInformation;
  loanApplication: ILoanApplication;
  incode?: IIncodeSession;
}

export interface ICreditLineFinancialForm extends IBaseFinancialForm {
  type: FinancialFormTypeEnum.CREDIT_LINE;
  phone: string;
  dialCode: string;
  fullAddress: TFullAddress;
  personalReferences?: TPersonalReferences;
  loanIdPayjoy?: string;
  collectionResponsible?: string;
  downPayment?: number;
  loanDuration?: number;
  zapsignContractId?: string;
  tier?: string;
  maxPeriodicPayment?: number;
  maxFinancingAmount?: number;
  whitelistName?: string;
  sellResponsible?: string;
  canceledReason?: string;
  appointment?: {
    scheduledAt: string;
    deliverOzonio: string;
  };
}

export type IFinancialForm = ILoanFinancialForm | ICreditLineFinancialForm;
export type TFinancialForm = IFinancialForm;

export const isLoanFinancialForm = (
  form: IFinancialForm | null | undefined
): form is ILoanFinancialForm => form?.type === FinancialFormTypeEnum.LOAN;

export const isCreditLineFinancialForm = (
  form: IFinancialForm | null | undefined
): form is ICreditLineFinancialForm =>
  form?.type === FinancialFormTypeEnum.CREDIT_LINE;
